import * as L from 'leaflet';

function scalePoint(
  point: L.PointExpression | undefined,
  scale: number
): L.PointExpression | undefined {
  if (!point) return point;
  const p = L.point(point as any);
  return L.point(Math.round(p.x * scale), Math.round(p.y * scale));
}

function createScaledLeafletIcon(
  icon: L.Icon<L.IconOptions>,
  scale: number
): L.Icon<L.IconOptions> {
  const opts = icon.options;
  const size = opts.iconSize ? L.point(opts.iconSize as any) : null;
  const shadowSize = opts.shadowSize ? L.point(opts.shadowSize as any) : null;

  return L.icon({
    ...opts,
    iconSize: size
      ? L.point(Math.round(size.x * scale), Math.round(size.y * scale))
      : undefined,
    iconAnchor: scalePoint(opts.iconAnchor, scale),
    popupAnchor: scalePoint(opts.popupAnchor, scale) as any,
    tooltipAnchor: scalePoint((opts as any).tooltipAnchor, scale) as any,
    shadowSize: shadowSize
      ? L.point(
          Math.round(shadowSize.x * scale),
          Math.round(shadowSize.y * scale)
        )
      : undefined,
    shadowAnchor: scalePoint(opts.shadowAnchor, scale) as any,
  });
}

function computeTransformOrigin(
  iconSize?: L.PointExpression,
  iconAnchor?: L.PointExpression
): string {
  const size = iconSize ? L.point(iconSize as any) : L.point(32, 32);
  const anchor = iconAnchor
    ? L.point(iconAnchor as any)
    : L.point(size.x / 2, size.y / 2);
  const x = (anchor.x / size.x) * 100;
  const y = (anchor.y / size.y) * 100;
  return `${x.toFixed(2)}% ${y.toFixed(2)}%`;
}

function createScaledDivIcon(icon: L.DivIcon, scale: number): L.DivIcon {
  const opts = icon.options;
  const origin = computeTransformOrigin(
    opts.iconSize as any,
    opts.iconAnchor as any
  );
  const html = `
    <div style="display:inline-block; transform: scale(${scale}); transform-origin: ${origin};">
      ${opts.html ?? ''}
    </div>
  `;

  return L.divIcon({
    ...opts,
    html,
    // mantenemos el iconSize para que el anclaje geográfico no cambie visualmente
  });
}

export function createScaledIcon(
  icon: L.Icon<L.IconOptions> | L.DivIcon,
  scale: number
): L.Icon<L.IconOptions> | L.DivIcon {
  // L.DivIcon no tiene una firma pública para diferenciar, usamos instanceof
  if ((icon as any) instanceof L.DivIcon) {
    return createScaledDivIcon(icon as L.DivIcon, scale);
  }
  return createScaledLeafletIcon(icon as L.Icon<L.IconOptions>, scale);
}

export function addHoverScaleToMarker(marker: L.Marker, scale = 1.15) {
  const originalIcon = (marker.options.icon ??
    L.icon({ iconUrl: 'assets/icons/frame.svg' })) as
    | L.Icon<L.IconOptions>
    | L.DivIcon;
  const hoverIcon = createScaledIcon(originalIcon, scale);

  marker.on('mouseover', () => {
    marker.setIcon(hoverIcon as any);
  });

  marker.on('mouseout', () => {
    marker.setIcon(originalIcon as any);
  });
}
